import React, { useState, useCallback, useEffect } from 'react';
import matterportService from '../services/matterportService';

/**
 * TEMPORARY dev-only button — floating, bottom-right.
 * Click to copy the current camera pose + sweep as a ready-to-paste
 * "camera" block for public/config/tourTranslations.json.
 *
 * Also finds the nearest tour snapshot SID so you know which stop the
 * values belong under.
 *
 * Remove this component (and its import in App.js) before production.
 */
const CameraPoseDebug = () => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'ok' | 'err'
  const [lastSid, setLastSid] = useState(null);

  const handleCopy = useCallback(async () => {
    try {
      const sdk = window._mpSdk;
      if (!sdk) throw new Error('SDK not ready');

      const pose = await sdk.Camera.getPose();
      const yaw = +pose.rotation.y.toFixed(2);
      const pitch = +pose.rotation.x.toFixed(2);
      const sweepId = pose.sweep || null;

      // Find the nearest tour snapshot SID (by sweep mapping)
      const snapToSweep = matterportService._snapToSweep || {};
      let matchSnapSid = null;
      for (const [snapSid, swSid] of Object.entries(snapToSweep)) {
        if (swSid === sweepId) { matchSnapSid = snapSid; break; }
      }
      setLastSid(matchSnapSid || sweepId);

      const cameraBlock = {
        pitch,
        yaw,
        panDegrees: 0,
        panSpeed: 1,
        flyTime: 1200,
        imageDuration: 4000,
        dwellTime: 8000,
        zoom: null,
        sweepId,
      };

      // Prettified JSON comment header + camera block
      const out = [
        `// snapshot SID: ${matchSnapSid || '(no matching snapshot — using sweep)'}`,
        `// sweep:        ${sweepId}`,
        `// yaw: ${yaw}°   pitch: ${pitch}°`,
        `"camera": ${JSON.stringify(cameraBlock, null, 2)}`,
      ].join('\n');

      let copied = false;
      // Try the modern async Clipboard API first (requires focus + secure context)
      try {
        if (navigator.clipboard && window.isSecureContext) {
          window.focus();
          await navigator.clipboard.writeText(out);
          copied = true;
        }
      } catch (clipErr) {
        console.warn('📋 clipboard.writeText failed, trying fallback:', clipErr?.message);
      }
      // Fallback — hidden textarea + execCommand('copy') works even when
      // document focus is inside the Matterport iframe.
      if (!copied) {
        const ta = document.createElement('textarea');
        ta.value = out;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '0';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        try {
          copied = document.execCommand('copy');
        } catch (_) { copied = false; }
        document.body.removeChild(ta);
      }

      console.log('📋 Camera pose:\n' + out);
      if (!copied) {
        // Last-resort: dump to console so dev can copy from there
        console.warn('📋 Clipboard blocked — copy the block above from the console.');
        throw new Error('Clipboard blocked');
      }
      setStatus('ok');
      setTimeout(() => setStatus('idle'), 1800);
    } catch (e) {
      console.error('📋 Copy failed:', e);
      setStatus('err');
      setTimeout(() => setStatus('idle'), 2200);
    }
  }, []);

  // Keyboard shortcut: Alt+Shift+C triggers the same copy action.
  // (Ctrl+Shift+C is reserved by Chrome DevTools' element picker.)
  // Capture phase so the Matterport iframe can't swallow the event.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === 'c' || e.key === 'C') && e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        handleCopy();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [handleCopy]);

  // No UI — the component mounts solely to register the Alt+Shift+C hotkey.
  // A brief toast confirms the copy succeeded/failed.
  if (status === 'idle') return null;
  const toastColor = status === 'ok' ? '#22c55e' : '#f87171';
  const toastText  = status === 'ok'
    ? `✅ Camera pose copied${lastSid ? ` — ${lastSid}` : ''}`
    : '⚠️ Copy failed — see console';
  return (
    <div
      style={{
        position: 'fixed',
        top: 80,
        right: 12,
        zIndex: 99999,
        padding: '8px 12px',
        background: 'rgba(20, 20, 30, 0.92)',
        color: toastColor,
        border: `1px solid ${toastColor}66`,
        borderRadius: 8,
        font: '600 12px/1.3 system-ui, sans-serif',
        boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(8px)',
        pointerEvents: 'none',
      }}
    >
      {toastText}
    </div>
  );
};

export default CameraPoseDebug;
